import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Auth interceptor — anexa o token em todas as requisições ──
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

// ── Refresh automático quando token expira (401) ──
api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem('refresh_token');
        if (!refresh) throw new Error('no refresh');
        const { data } = await axios.post(`${API_URL}/auth/refresh/`, { refresh });
        localStorage.setItem('access_token', data.access);
        original.headers['Authorization'] = `Bearer ${data.access}`;
        return api(original);
      } catch {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// ── Autenticação ──
export const login = (username, password) =>
  axios.post(`${API_URL}/auth/token/`, { username, password });
export const getMe = () => api.get('/auth/me/');

// ── Departamentos ──
export const getDepartamentos = (params) => api.get('/departamentos/', { params });
export const createDepartamento = (data) => api.post('/departamentos/', data);
export const updateDepartamento = (id, data) => api.put(`/departamentos/${id}/`, data);
export const deleteDepartamento = (id) => api.delete(`/departamentos/${id}/`);

// ── Funcionários ──
export const getFuncionarios = (params) => api.get('/funcionarios/', { params });
export const getFuncionariosResumo = () => api.get('/funcionarios/resumo/');
export const getFuncionario = (id) => api.get(`/funcionarios/${id}/`);
export const createFuncionario = (data) => api.post('/funcionarios/', data, {
  headers: { 'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json' },
});
export const updateFuncionario = (id, data) => api.patch(`/funcionarios/${id}/`, data, {
  headers: { 'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json' },
});
export const deleteFuncionario = (id) => api.delete(`/funcionarios/${id}/`);

// ── Jornadas ──
export const getJornadas = (params) => api.get('/jornadas/', { params });
export const getJornadaById = (id) => api.get(`/jornadas/${id}/`);
export const createJornada = (data) => api.post('/jornadas/', data);
export const updateJornada = (id, data) => api.put(`/jornadas/${id}/`, data);
export const deleteJornada = (id) => api.delete(`/jornadas/${id}/`);

// ── Registros de Ponto ──
export const getRegistros = (params) => api.get('/registros/', { params });
export const getRegistrosResumoMes = (params) => api.get('/registros/resumo-mes/', { params });
export const createRegistro = (data) => api.post('/registros/', data);
export const updateRegistro = (id, data) => api.put(`/registros/${id}/`, data);
export const deleteRegistro = (id) => api.delete(`/registros/${id}/`);
export const getDashboard = () => api.get('/registros/dashboard/');

// ── Afastamentos ──
export const getAfastamentos = (params) => api.get('/afastamentos/', { params });
export const createAfastamento = (data) => api.post('/afastamentos/', data, {
  headers: { 'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json' },
});
export const updateAfastamento = (id, data) => api.patch(`/afastamentos/${id}/`, data, {
  headers: { 'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json' },
});
export const deleteAfastamento = (id) => api.delete(`/afastamentos/${id}/`);

// ── Saldo de Horas ──
export const getSaldoHora = (registroId) => api.get('/saldo-horas/', { params: { registro: registroId } });
export const getSaldosHora = (params) => api.get('/saldo-horas/', { params });
export const saveSaldoHora = (data) => api.post('/saldo-horas/', data, {
  headers: { 'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json' },
});
export const updateSaldoHora = (id, data) => api.put(`/saldo-horas/${id}/`, data, {
  headers: { 'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json' },
});
export const deleteSaldoHora = (id) => api.delete(`/saldo-horas/${id}/`);

// ── Feriados ──
export const getFeriados = (params) => api.get('/feriados/', { params });
export const createFeriado = (data) => api.post('/feriados/', data);
export const updateFeriado = (id, data) => api.put(`/feriados/${id}/`, data);
export const deleteFeriado = (id) => api.delete(`/feriados/${id}/`);
export const getFeriadosCalendario = (params) => api.get('/feriados/calendario/', { params });

// ── Importação ──
export const importarJornada = (formData) =>
  api.post('/importar-jornada/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// ── Relatório ──
export const getRelatorio = (params) => api.get('/relatorio/', { params });
export const exportarRelatorio = (params) => {
  const token = localStorage.getItem('access_token');
  const query = new URLSearchParams({ ...params, token }).toString();
  window.open(`${API_URL}/relatorio/exportar/?${query}`, '_blank');
};

// ── Mapa VR ──
export const getMapaVR = (params) => api.get('/mapa-vr/', { params });
export const exportarMapaVR = (params) => {
  const token = localStorage.getItem('access_token');
  const query = new URLSearchParams({ ...params, token }).toString();
  window.open(`${API_URL}/mapa-vr/exportar/?${query}`, '_blank');
};

// ── Modelo Excel ──
export const downloadModeloExcel = () => {
  window.open(`${API_URL}/modelo-excel/`, '_blank');
};

// ── Jornada Extra ──
export const getJornadasExtra = (params) => api.get('/jornadas-extra/', { params });
export const createJornadaExtra = (data) => api.post('/jornadas-extra/', data);
export const deleteJornadaExtra = (id) => api.delete(`/jornadas-extra/${id}/`);

// ── Histórico de Importações ──
export const getHistoricoImportacoes = () => api.get('/historico-importacoes/');

// ── VR Apurações ──
export const getVRApuracoes   = (params) => api.get('/vr-apuracoes/', { params });
export const getVRApuracao    = (id)     => api.get(`/vr-apuracoes/${id}/`);
export const createVRApuracao = (data)   => api.post('/vr-apuracoes/', data);
export const fecharVRApuracao = (id)     => api.post(`/vr-apuracoes/${id}/fechar/`);
export const deleteVRApuracao = (id)     => api.delete(`/vr-apuracoes/${id}/`);

export default api;
